<template>
  <v-dialog v-model="dialog" max-width="1300px" scrollable persistent>
    <v-card>
      <v-card-title class="success white--text">
        <v-icon left color="white">mdi-cart-plus</v-icon>
        <span class="text-h6">Create Direct Purchase</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-form ref="form" v-model="valid">
          <v-row dense>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="form.supplier_id"
                :items="suppliers"
                item-text="supplier_name"
                item-value="id"
                label="Supplier *"
                :rules="[(v) => !!v || 'Supplier is required']"
                prepend-icon="mdi-truck"
                outlined
                dense
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="form.internal_purchase_order_id"
                :items="internal_purchase_orders"
                :item-text="prLabel"
                item-value="id"
                label="Purchase Request (Optional)"
                prepend-icon="mdi-file-document-outline"
                outlined
                dense
                clearable
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-switch
                v-model="form.is_vat_inclusive"
                :label="
                  form.is_vat_inclusive ? 'VAT Inclusive' : 'VAT Exclusive'
                "
                color="success"
                dense
              ></v-switch>
            </v-col>
            <v-col cols="12" md="6">
              <v-textarea
                v-model="form.remarks"
                label="Remarks"
                outlined
                dense
                rows="2"
                auto-grow
              ></v-textarea>
            </v-col>
          </v-row>

          <v-divider class="my-3"></v-divider>

          <div class="d-flex align-center mb-3">
            <h3 class="mb-0">Items</h3>
            <v-spacer></v-spacer>
            <v-btn color="primary" small @click="addItem">
              <v-icon small left>mdi-plus</v-icon>
              Add Item
            </v-btn>
          </div>

          <v-simple-table v-if="items.length > 0">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Item Name *</th>
                  <th style="width: 100px">Qty *</th>
                  <th style="width: 100px">Unit *</th>
                  <th style="width: 140px">Unit Cost *</th>
                  <th>Description</th>
                  <th class="text-right" style="width: 120px">Total</th>
                  <th style="width: 50px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in items" :key="index">
                  <td>{{ index + 1 }}</td>
                  <td>
                    <v-combobox
                      outlined
                      v-model="item.item_name"
                      :items="prItemNames"
                      dense
                      hide-details
                      placeholder="Item name"
                      :rules="[(v) => !!v || 'Required']"
                      :append-icon="prItemNames.length ? '$dropdown' : ''"
                      @change="onItemNamePicked(item, $event)"
                    >
                      <template v-slot:no-data>
                        <v-list-item>
                          <v-list-item-content>
                            <v-list-item-title class="text-caption">
                              {{
                                form.internal_purchase_order_id
                                  ? "No items on this Purchase Request — type to add manually."
                                  : "Link a Purchase Request to choose its items, or type manually."
                              }}
                            </v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </template>
                    </v-combobox>
                  </td>
                  <td>
                    <v-text-field
                      outlined
                      v-model.number="item.qty"
                      type="number"
                      dense
                      hide-details
                      min="1"
                      :rules="[(v) => v > 0 || 'Required']"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      outlined
                      v-model="item.unit"
                      dense
                      hide-details
                      placeholder="PCS"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      outlined
                      v-model.number="item.unit_cost"
                      type="number"
                      dense
                      hide-details
                      min="0"
                      prefix="₱"
                      :rules="[(v) => v >= 0 || 'Required']"
                    ></v-text-field>
                  </td>
                  <td>
                    <v-text-field
                      outlined
                      v-model="item.item_description"
                      dense
                      hide-details
                      placeholder="Optional"
                    ></v-text-field>
                  </td>
                  <td class="text-right font-weight-bold">
                    ₱{{
                      formatCurrency((item.qty || 0) * (item.unit_cost || 0))
                    }}
                  </td>
                  <td>
                    <v-btn
                      icon
                      x-small
                      color="error"
                      @click="removeItem(index)"
                    >
                      <v-icon x-small>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="6" class="text-right">
                    <strong>Grand Total:</strong>
                  </td>
                  <td class="text-right">
                    <strong class="text-h6"
                      >₱{{ formatCurrency(grandTotal) }}</strong
                    >
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </template>
          </v-simple-table>

          <v-alert v-else type="info" outlined dense class="mt-2">
            No items added yet. Click "Add Item" to start.
          </v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="close">Cancel</v-btn>
        <v-btn
          color="success"
          @click="submit"
          :loading="submitting"
          :disabled="!valid || items.length === 0"
        >
          <v-icon left>mdi-check</v-icon>
          Create Purchase Order
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "DirectPurchaseDialog",

  data() {
    return {
      dialog: false,
      valid: false,
      submitting: false,
      form: {
        supplier_id: null,
        internal_purchase_order_id: null,
        is_vat_inclusive: false,
        remarks: "",
      },
      items: [],
      lastPrDescription: "",
    };
  },

  watch: {
    // Auto-fill Remarks from the linked Purchase Request's description.
    // Only overwrites when Remarks is empty or still holds the previously
    // auto-filled PR description, so manually typed remarks are preserved.
    "form.internal_purchase_order_id"() {
      const pr = this.internal_purchase_orders.find(
        (p) => p.id === this.form.internal_purchase_order_id
      );
      const newDesc = pr && pr.description ? pr.description : "";
      if (!this.form.remarks || this.form.remarks === this.lastPrDescription) {
        this.form.remarks = newDesc;
      }
      this.lastPrDescription = newDesc;
    },
  },

  computed: {
    ...mapGetters({
      suppliers: "supplier/suppliers",
      internal_purchase_orders:
        "internal_purchase_order/internal_purchase_orders",
    }),

    grandTotal() {
      return this.items.reduce((sum, item) => {
        return sum + (item.qty || 0) * (item.unit_cost || 0);
      }, 0);
    },

    // Line items of the currently linked Purchase Request (empty when none linked)
    prItems() {
      const pr = this.internal_purchase_orders.find(
        (p) => p.id === this.form.internal_purchase_order_id
      );
      return pr && Array.isArray(pr.items) ? pr.items : [];
    },

    // Item-name options offered in the combobox (deduped)
    prItemNames() {
      return [
        ...new Set(
          this.prItems.map((i) => i.item_name).filter((name) => !!name)
        ),
      ];
    },
  },

  methods: {
    ...mapActions({
      create_direct_purchase: "purchase_order/create_direct_purchase",
      get_internal_purchase_orders:
        "internal_purchase_order/get_internal_purchase_orders",
    }),

    open() {
      this.dialog = true;
      this.resetForm();
      this.get_internal_purchase_orders().catch(() => {});
    },

    close() {
      this.dialog = false;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        supplier_id: null,
        internal_purchase_order_id: null,
        is_vat_inclusive: false,
        remarks: "",
      };
      this.items = [];
      this.lastPrDescription = "";
      this.submitting = false;
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    addItem() {
      this.items.push({
        item_name: "",
        qty: 1,
        unit: "PCS",
        unit_cost: 0,
        item_description: "",
      });
    },

    removeItem(index) {
      this.items.splice(index, 1);
    },

    // When a PR item name is picked (not free-typed), prefill Qty/Unit from the PR line
    onItemNamePicked(item, value) {
      if (!value) return;
      const match = this.prItems.find((i) => i.item_name === value);
      if (!match) return; // manual entry — leave qty/unit as-is
      if (match.qty) item.qty = match.qty;
      if (match.unit) item.unit = match.unit;
    },

    formatCurrency(amount) {
      if (!amount && amount !== 0) return "0.00";
      return parseFloat(amount).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    prLabel(pr) {
      const num = `PR-${String(pr.id).padStart(4, "0")}`;
      const date = pr.po_date ? new Date(pr.po_date).toLocaleDateString() : "";
      const desc = pr.description ? ` — ${pr.description}` : "";
      return date ? `${num} (${date})${desc}` : `${num}${desc}`;
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      // Validate items
      const invalidItems = this.items.filter(
        (item) =>
          !item.item_name ||
          !item.qty ||
          item.qty <= 0 ||
          !item.unit ||
          item.unit_cost < 0
      );
      if (invalidItems.length > 0) {
        this.$emit(
          "notification",
          "Please fill in all required item fields",
          "error"
        );
        return;
      }

      this.submitting = true;
      try {
        await this.create_direct_purchase({
          supplier_id: this.form.supplier_id,
          internal_purchase_order_id: this.form.internal_purchase_order_id,
          is_vat_inclusive: this.form.is_vat_inclusive,
          remarks: this.form.remarks,
          items: this.items,
        });

        this.$emit(
          "notification",
          "Direct purchase order created successfully!",
          "success"
        );
        this.$emit("created");
        this.close();
      } catch (error) {
        console.error("Error creating direct purchase:", error);
        this.$emit(
          "notification",
          error.response?.data?.message || "Failed to create direct purchase",
          "error"
        );
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
